import React, {useEffect, useRef, useCallback} from 'react';
import {useIntl} from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentContainer from '../../../components/PaymentContainer/PaymentContainer';
import RenewalHelpCard from '../components/RenewalHelpCard';
import {useRenewalClick} from '../context/RenewalClickContext';
import {SCREENS, RenewalJourneyEvent, RenewalJourneyLocation} from '../constants/renewalClick';
import {submitRenewalPayment, fireRenewalJourneyActivity} from '../api/renewalClickApi';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('he-IL', {style: 'currency', currency: 'ILS', maximumFractionDigits: 0}).format(amount);

const styles = {
  container: {px: 2, pb: 5, maxWidth: 560, mx: 'auto'},
  title: (theme) => ({
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: 'secondary.main',
    mb: 0.5,
    textAlign: 'center',
  }),
  subtitle: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    color: 'text.secondary',
    mb: 2.5,
    textAlign: 'center',
  }),
  paymentWrapper: {
    width: '100%',
    maxWidth: {xs: '100%', sm: 480},
    mx: 'auto',
    '& [class*="purchaseContainer"]': {
      padding: 0,
      marginTop: 0,
    },
    // Force single-column layout in our narrow container
    '& .MuiGrid-container > .MuiGrid-root': {
      flexBasis: '100%',
      maxWidth: '100%',
    },
    '& [class*="purchaseBtnWrapper"]': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  disclaimer: {
    borderRadius: '12px',
    p: 1.75,
    mb: 2,
    display: 'flex',
    gap: 1,
    alignItems: 'flex-start',
    bgcolor: 'background.default',
  },
  disclaimerText: (theme) => ({fontSize: theme.typography.pxToRem(13), color: 'text.secondary', lineHeight: 1.5}),
  backToOffer: (theme) => ({
    display: 'block',
    mx: 'auto',
    mt: 2,
    mb: 2,
    p: 0,
    border: 0,
    background: 'none',
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  }),
  totalSection: {
    border: 1,
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2,
    mb: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: (theme) => ({
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
    color: 'text.primary',
  }),
  totalAmount: (theme) => ({
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: 'secondary.main',
  }),
  securePayment: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    mt: 1.25,
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
  }),
};

const PaymentScreen = () => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({id}, values);
  const {
    shortLinkKey, opportunityId, pricing,
    paymentProcessing, paymentError,
    setPaymentProcessing, setPaymentError, setScreen,
  } = useRenewalClick();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current && opportunityId) {
      hasTracked.current = true;
      fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.REACHED_PAYMENT, {location: RenewalJourneyLocation.PAYMENT});
    }
  }, [opportunityId]);

  const onPaymentSubmit = useCallback(async (values, {setErrors}) => {
    setPaymentProcessing(true);
    setPaymentError(null);

    const paymentData = {
      ...values,
      month: String(values.month).padStart(2, '0'),
      year: String(values.year),
    };

    try {
      const response = await submitRenewalPayment(shortLinkKey, paymentData);
      if (response.isSuccess) {
        fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.PAYMENT_COMPLETED, {location: RenewalJourneyLocation.PAYMENT});
        setScreen(SCREENS.SIGNING_PENDING);
      } else {
        const errorMsg = response.friendlyError
          ? intl.formatMessage({id: response.friendlyError})
          : intl.formatMessage({id: 'renewalClick.payment.error'});
        setErrors({creditCardNumber: errorMsg});
        setPaymentError(errorMsg);
      }
    } catch {
      const msg = intl.formatMessage({id: 'renewalClick.payment.error'});
      setErrors({creditCardNumber: msg});
      setPaymentError(msg);
    } finally {
      setPaymentProcessing(false);
    }
  }, [shortLinkKey, intl, setPaymentProcessing, setPaymentError, setScreen, opportunityId]);

  const handleBackToOffer = useCallback(() => {
    setScreen(SCREENS.OFFER);
  }, [setScreen]);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        {m('renewalClick.payment.title')}
      </Typography>
      <Typography sx={styles.subtitle}>
        {m('renewalClick.payment.subtitle', {amount: formatCurrency(pricing.totalPremium)})}
      </Typography>

      {paymentError && (
        <Alert severity='error' sx={{mb: 2}}>{paymentError}</Alert>
      )}

      <Box sx={styles.paymentWrapper}>
        <PaymentContainer
          onPaymentSubmit={onPaymentSubmit}
          isVerifyingPayment={paymentProcessing}
          isUseCardForBoth
          isFirstCard
          policyType='מקיף'
          isAsset={false}
          displayUserOwnCardFields
        />
      </Box>

      {/* Bottom info row */}
      <Typography sx={styles.securePayment}>
        <LockOutlinedIcon sx={{fontSize: 14}} />
        {m('renewalClick.securePayment')}
      </Typography>

      {/* Cancellation note — same visual as OfferScreen disclaimer */}
      <Box sx={styles.disclaimer}>
        <Typography sx={styles.disclaimerText}>
          {m('renewalClick.payment.cancellationText')}
        </Typography>
        <Tooltip title={m('renewalClick.offer.serviceFeeDisclaimer')} arrow>
          <InfoOutlinedIcon sx={{color: 'primary.main', fontSize: 22, flexShrink: 0, mt: 0.25, cursor: 'pointer'}} />
        </Tooltip>
      </Box>

      {/* Back to offer */}
      <Typography
        component='button'
        type='button'
        onClick={() => setScreen(SCREENS.OFFER)}
        sx={styles.backToOffer}
      >
        {m('renewalClick.payment.backToOffer')}
      </Typography>

      {/* Help card */}
      <Box sx={{mb: 2.5}}>
        <RenewalHelpCard
          title={m('renewalClick.helpTitle')}
          description={m('renewalClick.helpDescription')}
        />
      </Box>

    </Box>
  );
};

export default PaymentScreen;
